/*@flow*/
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import MostActiveCategories from './MostActiveCategories';
import { useEffect, useState, useContext } from 'react';
import Category from '../Category';
import { extractVectorTiles } from 'lib/getCartoTiles';
import Present from 'lib/Present';
import hash from 'lib/hash';
import keepNonEmptyKeys from 'lib/keepNonEmptyKeys';
import useCartoMap from 'lib/hooks/useCartoMap';
import { ChartDeckContext } from '../contexts';
import ColumnGrid from '../ColumnGrid';
import ClearButton from '../ClearButton';
import mkOnSelectWith from '../mkOnSelectWith';
import { type VectorTileUrls } from 'lib/getCartoMap';
import mkPopupEvents from '../mkPopupEvents';
import { zippedColors } from './colors';

let CHART_NAME;

const DATAVIEW_1 = 'category-widget-2';
const ANALYSIS_1 = 'all_complaints';
const LAYER_1 = 'little-red-dots';

const QUERY_TMPL = `
  SELECT cartodb_id, the_geom_webmercator, complaint_type, date_trunc('month', created_date) as month
  FROM circular_city_study_area_311_complaints
`;

const INIT_CONFIG = {
  buffersize: {
    mvt: 0,
  },
  analyses: [
    {
      id: ANALYSIS_1,
      type: 'source',
      params: {
        query: QUERY_TMPL,
      },
    },
  ],
  layers: [
    {
      id: LAYER_1,
      type: 'cartodb',
      options: {
        source: {
          id: ANALYSIS_1,
        },
      },
    },
  ],
  dataviews: {
    [DATAVIEW_1]: {
      type: 'aggregation',
      source: {
        id: ANALYSIS_1,
      },
      options: {
        aggregation: 'count',
        column: 'complaint_type',
      },
    },
  },
};

const getMapboxTiles = ({
  mapTiles,
  cartoLayerName,
}: {
  mapTiles: VectorTileUrls,
  cartoLayerName: string,
}) => [
  {
    id: `${cartoLayerName}-source`,
    type: 'source',
    config: {
      type: 'vector',
      tiles: mapTiles,
      maxzoom: 18,
    },
  },
  {
    id: `${cartoLayerName}-layer`,
    type: 'layer',
    config: {
      type: 'circle',
      source: `${cartoLayerName}-source`,
      // Note: this needs to be the id of the cartodb layer.
      'source-layer': cartoLayerName,
      paint: {
        'circle-radius': ['step', ['zoom'], 3, 15, 4],
        'circle-color': [
          'match',
          ['get', 'complaint_type'],
          ...zippedColors,
          'red',
        ],
        'circle-opacity': 0.05,
      },
    },
    events: mkPopupEvents(
      v => `
      <div>
        <h4>${CHART_NAME}</h4>
        <div><b>Complaint Type:</b> ${v.complaint_type}</div>
      </div>
    `
    ),
  },
];

export default (props: { name: string }) => {
  CHART_NAME = props.name;
  const [params, setParams] = useState({});
  const [showLayers, setShowLayers] = useState(false);
  const mapConfig = useCartoMap(params, INIT_CONFIG);
  const mapTiles = mapConfig ? extractVectorTiles(mapConfig) : null;
  const { addMapTiles, removeMapTiles } = useContext(ChartDeckContext);

  const toggleMapLayers = () => {
    if (!mapTiles) return;
    const tiles = getMapboxTiles({
      mapTiles,
      cartoLayerName: LAYER_1,
    });

    if (showLayers) {
      addMapTiles(tiles);
    } else {
      removeMapTiles(tiles);
    }
  };

  useEffect(() => {
    toggleMapLayers();
  }, [mapTiles && mapTiles[0], showLayers, hash(params)]);

  useEffect(() => {
    setTimeout(() => Present.is(params.filters) && setShowLayers(true), 100);
  }, [hash(params.filters)]);

  return (
    <div
      css={css(
        `
      display: grid;
      grid-template-rows: 2rem 1fr;
      grid-column-gap: 10px;
      `
      )}
    >
      <div css={css('padding-top: 5px; padding-bottom: 5px;')}>
        <label css={css('margin: 0; cursor: pointer;')}>
          <input
            type="checkbox"
            checked={showLayers}
            onChange={e => setShowLayers(e.target.checked)}
          />{' '}
          Show map layers
        </label>
        <ClearButton
          filters={params.filters}
          onClick={() => {
            setParams({
              filters: {},
            });
          }}
        />
      </div>
      <ColumnGrid columns="1fr 30%">
        <div>
          <MostActiveCategories
            mapConfig={mapConfig}
            analysisId={ANALYSIS_1}
            onSelect={mkOnSelectWith(setParams, DATAVIEW_1)}
          />
        </div>
        <div>
          <Category
            title="Complaint Types"
            id={DATAVIEW_1}
            mapConfig={mapConfig}
            onFiltersChanged={({ accepted }) => {
              const filters = keepNonEmptyKeys({
                accept: accepted,
                reject: [],
              });
              if (Present.is(filters)) {
                setParams({
                  filters: {
                    dataviews: {
                      [DATAVIEW_1]: filters,
                    },
                  },
                });
              } else if (Present.is(params)) {
                setParams({
                  filters: {},
                });
              }
            }}
          />
        </div>
      </ColumnGrid>
    </div>
  );
};
